import React, { useRef, useState } from 'react'
import * as yup from 'yup'
import { ErrorMessage, Field, FieldArray, Form, Formik } from 'formik'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import AddIcon from '@mui/icons-material/Add'
import AdxInput from 'components/custom/AdxInput'
import InputAdornment from '@mui/material/InputAdornment'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import { useMutation, useQueryClient } from 'react-query'
import { postNewClaimRoutes } from 'services/api/modules/claim/claimRoute'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import AdxButton from 'components/custom/AdxButton'
import ClaimApprovalPreview from './ClaimApprovalPreview'

const getDuplicateApprovalChains = (claimRanges, approvalRoutes) => {
  const routeCounts = claimRanges.reduce((counts, claimRange) => {
    if (!claimRange.approval_route_id) return counts

    const id = String(claimRange.approval_route_id)
    return {
      ...counts,
      [id]: (counts[id] || 0) + 1,
    }
  }, {})

  return Object.entries(routeCounts)
    .filter(([, count]) => count > 1)
    .map(([id]) => approvalRoutes.find((route) => String(route.id) === id)?.name)
    .filter(Boolean)
}

const formatAmount = (value) => {
  if (value === null || value === '' || Number.isNaN(Number(value))) {
    return null
  }

  return `RM ${parseFloat(value).toFixed(2)}`
}

const getPreviewTabLabel = (claimRange, index, total) => {
  if (total === 1) return 'All claim amounts'

  const from = formatAmount(claimRange?.amount_from)
  const to = formatAmount(claimRange?.amount_to)

  if (index === total - 1 || claimRange?.amount_to === null) {
    return `${from || 'RM 0.00'}+`
  }

  return `${from || 'RM 0.00'} - ${to || 'Next tier'}`
}

const getRuleLabel = (index, total) => {
  if (total === 1) return 'All claim amounts'

  return `Claim Rule ${index + 1}`
}

const ClaimRangeForm = ({
  handleClose,
  disabled,
  policyData,
  approvalRoutes = [],
  reimbursementAdmins = [],
}) => {
  const [isErrorModalOpen, setErrorModalOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [selectedPreviewIndex, setSelectedPreviewIndex] = useState(0)

  const handleErrorOpen = () => {
    setErrorModalOpen(true)
  }
  const handleErrorClose = () => {
    setErrorModalOpen(false)
  }

  const validationSchema = yup.object().shape({
    claimRange: yup.array().of(
      yup.object().shape({
        amount_from: yup.number().typeError('Invalid amount').required('Amount is required'),
        amount_to: yup
          .number()
          .typeError('Invalid amount')
          .max(999999.99, 'Max RM 999,999.99')
          .moreThan(yup.ref('amount_from'), 'Invalid max amount')
          .nullable(),
        approval_route_id: yup.string().required('Approval chain is required'),
      })
    ),
  })

  // report submission API
  const formikRef = useRef()
  const queryClient = useQueryClient()
  const {
    isLoading: submitLoading,
    mutate: submitMutate,
  } = useMutation((values) => postNewClaimRoutes(values), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('claimRouteData')
      await queryClient.invalidateQueries('approverRoutesData')
      handleClose()
    },
    onError: async (error) => {
      const { errors } = error.data

      Object.keys(errors).forEach(function (key) {
        setErrorMessage(errors[key][0])
      })

      handleErrorOpen()
    },
  })

  return (
    <>
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={{
          claimRange:
            policyData.length !== 0
              ? policyData?.map((item, index) => ({
                  id: item?.id,
                  amount_from: item?.amount_from,
                  amount_to: item?.amount_to,
                  approval_route_id: item?.approval_route_id,
                }))
              : [
                  { amount_from: '0.00', amount_to: '', approval_route_id: '' },
                  {
                    amount_from: '',
                    amount_to: null,
                    approval_route_id: '',
                  },
                ],
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          submitMutate({ data: values.claimRange })
        }}
      >
        {({ values, handleChange, handleBlur, setFieldValue, isValid, errors, touched }) => {
          const duplicateApprovalChains = getDuplicateApprovalChains(values.claimRange, approvalRoutes)
          const previewIndex = Math.min(selectedPreviewIndex, values.claimRange.length - 1)
          const selectedPreviewRule = values.claimRange[previewIndex]

          return (
            <Form>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, py: 2 }}>
                    <Box
                      borderRadius="4.5px"
                      bgcolor="gray-background.main"
                      px={2}
                      py={1.5}
                    >
                      <Typography variant="body1" color="dark-text.main" fontWeight="bold">
                        Set how claim amounts choose approvers
                      </Typography>
                      <Typography variant="body2" color="light-gray-text.main">
                        Keep one rule if every claim follows the same approval chain. Add
                        amount-based rules only when approval changes by claim amount.
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="light-gray-text.main">
                      Claim rules
                    </Typography>
                    <Box
                      width={1}
                      border={1}
                      borderColor="gray-line.main"
                      sx={{ borderRadius: '4.5px' }}
                    >
                      <FieldArray
                        name="claimRange"
                        render={(arrayHelpers) => (
                          <Box>
                            {values.claimRange.map((claimRange, index, fieldArray) => (
                              <Box
                                width={1}
                                key={index}
                                display="flex"
                                flexDirection={{ xs: 'column', md: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: null, lg: 'center' }}
                                borderBottom={1}
                                borderColor="gray-line.main"
                                paddingX={2}
                                paddingY={2}
                                boxSizing="border-box"
                                sx={{ gap: 1 }}
                              >
                                <Box
                                  width={1}
                                  display="flex"
                                  alignItems={{ xs: null, md: 'center' }}
                                  flexDirection={{ xs: 'column', md: 'row' }}
                                  sx={{ gap: { xs: 2, md: 1 } }}
                                >
                                  <Typography
                                    width={'10%'}
                                    variant="body1"
                                    color="light-gray-text.main"
                                  >
                                    {getRuleLabel(index, values.claimRange.length)}
                                  </Typography>
                                  <Box
                                    width={{ xs: 1, lg: '40%' }}
                                    display="flex"
                                    alignItems="center"
                                    sx={{ gap: 2 }}
                                  >
                                    <Field
                                      name={`claimRange.${index}.amount_from`}
                                      as={AdxInput}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      type="number"
                                      disabled
                                      inputProps={{
                                        min: '0.00',
                                        step: '0.01',
                                      }}
                                      startAdornment={
                                        <InputAdornment position="start">
                                          <Typography color="light-gray.main">RM</Typography>
                                        </InputAdornment>
                                      }
                                    />
                                    <Typography variant="body1" color="dark-text">
                                      to
                                    </Typography>
                                    {index !== fieldArray.length - 1 ? (
                                      <Field
                                        name={`claimRange.${index}.amount_to`}
                                        as={AdxInput}
                                        type="number"
                                        disabled={index !== fieldArray.length - 2}
                                        placeholder={
                                          values.claimRange[index].amount_to === null
                                            ? 'Infinity'
                                            : null
                                        }
                                        inputProps={{
                                          min: values.claimRange[index].amount_from,
                                          step: '0.01',
                                        }}
                                        errorMessage={
                                          <ErrorMessage
                                            name={`claimRange.${index}.amount_to`}
                                            component={AdxErrorMessage}
                                          />
                                        }
                                        // using settimeout due to rendering delay in field array
                                        isErrorShown={
                                          errors?.claimRange?.length &&
                                          errors?.claimRange[index]?.amount_to
                                        }
                                        onChange={(e) => {
                                          handleChange(e)
                                          if (index === fieldArray.length - 2) {
                                            setFieldValue(
                                              `claimRange.${
                                                values.claimRange.length - 1
                                              }.amount_from`,
                                              parseFloat(parseFloat(e.target.value) + 0.01).toFixed(
                                                2
                                              )
                                            )
                                          }
                                        }}
                                        onBlur={(e) => {
                                          handleBlur(e)
                                          setFieldValue(
                                            `claimRange.${index}.amount_to`,
                                            parseFloat(e.target.value).toFixed(2)
                                          )
                                        }}
                                        startAdornment={
                                          <InputAdornment position="start">
                                            <Typography
                                              color={
                                                index !== fieldArray.length - 1
                                                  ? 'light-gray.main'
                                                  : 'dark-text.main'
                                              }
                                            >
                                              RM
                                            </Typography>
                                          </InputAdornment>
                                        }

                                        // endAdornment={
                                        //   values.claimRange[index].amount_to ==
                                        //     null ||
                                        //   values.claimRange[index]
                                        //     .amount_to ? null : (
                                        //     <InputAdornment position="end">
                                        //       <AdxButton
                                        //         disabled={disabled}
                                        //         sx={{
                                        //           height: 0,
                                        //           minWidth: 0,
                                        //           paddingX: 1,
                                        //           paddingY: 1.5,
                                        //         }}
                                        //         size="small"
                                        //         onClick={(e) =>
                                        //           setFieldValue(
                                        //             `claimRange.${index}.amount_to`,
                                        //             Infinity
                                        //           )
                                        //         }
                                        //       >
                                        //         MAX
                                        //       </AdxButton>
                                        //     </InputAdornment>
                                        //   )
                                        // }
                                      />
                                    ) : (
                                      <AdxInput disabled value="Infinity" />
                                    )}
                                  </Box>
                                  <Box
                                    width={{ xs: 1, lg: '40%' }}
                                    display="flex"
                                    alignItems="center"
                                    columnGap={1}
                                  >
                                    <Typography variant="body1" color="light-gray-text.main">
                                      Approval chain
                                    </Typography>
                                    <Field
                                      name={`claimRange.${index}.approval_route_id`}
                                      as={AdxDropdown}
                                      onChange={(e) => {
                                        handleChange(e)
                                      }}
                                      onBlur={handleBlur}
                                      sx={{ width: { xs: 1, lg: 300 } }}
                                      renderValue={(option) => {
                                        const name = approvalRoutes.filter(
                                          (item) => item.id === option
                                        )[0].name

                                        return (
                                          <Box>
                                            <Typography variant="body1" color="dark-text">
                                              {name}
                                            </Typography>
                                          </Box>
                                        )
                                      }}
                                      errorMessage={
                                        <ErrorMessage
                                          name={`claimRange.${index}.approval_route_id`}
                                          component={AdxErrorMessage}
                                        />
                                      }
                                      // using settimeout due to rendering delay in field array
                                      isErrorShown={
                                        errors?.claimRange?.length &&
                                        errors?.claimRange[index]?.approval_route_id
                                      }
                                    >
                                      {approvalRoutes.map((item, indexRoute) => {
                                        const approverLayers = [
                                          item?.layer_one,
                                          item?.layer_two,
                                          item?.layer_three,
                                          item?.layer_four,
                                          item?.layer_five,
                                        ].filter(function (element) {
                                          return element !== null
                                        })
                                        return (
                                          <AdxMenuItem
                                            key={indexRoute}
                                            value={item.id}
                                            selectedvalue={
                                              values.claimRange[index].approval_route_id
                                            }
                                          >
                                            <Box>
                                              <Typography variant="body1" color="dark-text">
                                                {item.name}
                                              </Typography>
                                              <Breadcrumbs
                                                color="light-gray-text.main"
                                                separator=">"
                                                sx={{ fontSize: 14 }}
                                              >
                                                {approverLayers.map((item, indexName) => (
                                                  <Typography variant="body2" key={indexName}>
                                                    {item.name}
                                                  </Typography>
                                                ))}
                                              </Breadcrumbs>
                                            </Box>
                                          </AdxMenuItem>
                                        )
                                      })}
                                    </Field>
                                  </Box>
                                  <Box sx={{ width: { xs: 1, md: '10%' } }}>
                                    {index === fieldArray.length - 2 && fieldArray.length > 1 ? (
                                      <AdxButton
                                        variant={'ghost-destructive'}
                                        onClick={() => {
                                          const filteredItem = values?.claimRange?.filter(
                                            (_, filterIndex) => filterIndex !== index
                                          )

                                          if (index === 0) {
                                            filteredItem[0] = {
                                              ...filteredItem[0],
                                              amount_from: '0.00',
                                              amount_to: null,
                                            }
                                          } else {
                                            filteredItem[index] = {
                                              ...filteredItem[index],
                                              amount_from: parseFloat(
                                                parseFloat(
                                                  values.claimRange[index - 1].amount_to
                                                ) + 0.01
                                              ).toFixed(2),
                                            }
                                          }

                                          setFieldValue('claimRange', filteredItem)
                                        }}
                                        sx={{
                                          display: 'flex',
                                          gap: 1,
                                          alignItems: 'center',
                                          width: 1,
                                        }}
                                      >
                                        <Typography
                                          fontWeight="bold"
                                          color="error.main"
                                          sx={{ display: { xs: 'block', md: 'none' } }}
                                        >
                                          Delete
                                        </Typography>
                                        <RemoveCircleIcon
                                          sx={{
                                            cursor: 'pointer',
                                            fontSize: 20,
                                          }}
                                          color="error"
                                        />
                                      </AdxButton>
                                    ) : null}
                                  </Box>
                                </Box>
                              </Box>
                            ))}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <AdxButton
                                variant={'ghost'}
                                disabled={!isValid}
                                onClick={() => {
                                  if (values.claimRange.length === 1) {
                                    const currentRule = values.claimRange[0]
                                    setFieldValue('claimRange', [
                                      {
                                        ...currentRule,
                                        amount_from: '0.00',
                                        amount_to: '100.00',
                                      },
                                      {
                                        amount_from: '100.01',
                                        amount_to: null,
                                        approval_route_id: currentRule.approval_route_id,
                                      },
                                    ])
                                    return
                                  }

                                  arrayHelpers.insert(values.claimRange.length - 1, {
                                    amount_from:
                                      parseFloat(
                                        values.claimRange[values.claimRange.length - 2].amount_to
                                      ) + 0.01,
                                    amount_to: '',
                                    approval_route_id: '',
                                  })
                                  setFieldValue(
                                    `claimRange.${values.claimRange.length}.amount_from`,
                                    ''
                                  )
                                }}
                                sx={{
                                  pl: 2,
                                  display: 'flex',
                                  justifyContent: 'center',
                                  width: '100%',
                                  gap: 1,
                                }}
                              >
                                <AddIcon sx={{ fontSize: 20 }} />
                                <Typography
                                  variant="body1"
                                  color={!isValid ? 'gray-inactive-button.main' : 'alternate.main'}
                                >
                                  Add amount-based rule
                                </Typography>
                              </AdxButton>
                            </Box>
                          </Box>
                        )}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Box px={3} pb={2}>
                      <Box display="flex" flexDirection="column" gap={1.5}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Preview
                        </Typography>
                        {values.claimRange.length > 1 && (
                          <Box
                            borderBottom={1}
                            borderColor="gray-line.main"
                            sx={{ maxWidth: '100%' }}
                          >
                            <Tabs
                              value={previewIndex}
                              onChange={(_, value) => setSelectedPreviewIndex(value)}
                              variant="scrollable"
                              scrollButtons="auto"
                            >
                              {values.claimRange.map((claimRange, index) => (
                                <Tab
                                  key={index.toString()}
                                  label={getPreviewTabLabel(
                                    claimRange,
                                    index,
                                    values.claimRange.length
                                  )}
                                  sx={{ textTransform: 'none' }}
                                />
                              ))}
                            </Tabs>
                          </Box>
                        )}
                        <ClaimApprovalPreview
                          claimRange={selectedPreviewRule}
                          index={previewIndex}
                          total={values.claimRange.length}
                          approvalRoutes={approvalRoutes}
                          reimbursementAdmins={reimbursementAdmins}
                          compact
                        />
                        {duplicateApprovalChains.length > 0 && (
                          <Box
                            border={1}
                            borderColor="warning.main"
                            bgcolor="warning.light"
                            borderRadius="4.5px"
                            p={2}
                          >
                            <Typography variant="body2" color="warning.dark" fontWeight="bold">
                              Same approval chain used in multiple claim rules
                            </Typography>
                            <Typography variant="body2" color="warning.dark">
                              {duplicateApprovalChains.join(', ')} can be reused across tiers. If
                              the approval journey is identical for these amount ranges, you may
                              combine them into one rule.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column-reverse', md: 'row' },
                    gap: 1,
                    justifyContent: { xs: 'center', md: 'end' },
                    alignItems: 'center',
                    px: 3,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: 'gray-line.main',
                    bgcolor: 'primary.light',
                  }}
                >
                  <AdxButton
                    variant={'outlined'}
                    onClick={handleClose}
                    sx={{
                      width: { xs: 1, md: '100px' },
                    }}
                  >
                    Cancel
                  </AdxButton>
                  <AdxButton
                    variant={'contained'}
                    type="submit"
                    sx={{
                      width: { xs: 1, md: '200px' },
                    }}
                    onClick={() => null}
                    isLoading={submitLoading}
                    disabled={submitLoading}
                  >
                    Save
                  </AdxButton>
                </Box>
              </Box>
            </Form>
          )
        }}
      </Formik>
      {/* ERROR MODAL */}
      <AdxInfoModal
        variant="error"
        open={isErrorModalOpen}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            handleErrorClose()
          }
        }}
        handleClose={handleErrorClose}
        muiIcon="error"
        iconColor="error.main"
        title="An error occured"
        subtitle={errorMessage}
        footerButtons
        firstBtnLabel="Cancel"
        firstBtnHandle={handleErrorClose}
      ></AdxInfoModal>
    </>
  )
}

export default ClaimRangeForm
